import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { useSocket } from '../../contexts/SocketContext';

// Small dropdown for the board header: this board's mind maps + create new.
const BoardMapsMenu = ({ boardId }) => {
  const [open, setOpen] = useState(false);
  const [maps, setMaps] = useState([]);
  const [creating, setCreating] = useState(false);
  const [newName, setNewName] = useState('');
  const ref = useRef(null);
  const navigate = useNavigate();
  const socket = useSocket();

  const fetchMaps = useCallback(async () => {
    try {
      const res = await axios.get(`/api/mindmaps/board/${boardId}`, {
        headers: { 'x-auth-token': localStorage.getItem('token') }
      });
      setMaps(res.data);
    } catch (e) { /* board may simply have no maps */ }
  }, [boardId]);

  useEffect(() => { fetchMaps(); }, [fetchMaps]);

  useEffect(() => {
    if (!socket) return;
    const refresh = () => fetchMaps();
    socket.on('mindmap:created', refresh);
    socket.on('mindmap:deleted', refresh);
    return () => {
      socket.off('mindmap:created', refresh);
      socket.off('mindmap:deleted', refresh);
    };
  }, [socket, fetchMaps]);

  useEffect(() => {
    const close = (e) => { if (ref.current && !ref.current.contains(e.target)) setOpen(false); };
    document.addEventListener('mousedown', close);
    return () => document.removeEventListener('mousedown', close);
  }, []);

  const createMap = async () => {
    if (!newName.trim()) return;
    try {
      const res = await axios.post('/api/mindmaps', { boardId, name: newName.trim() }, {
        headers: { 'x-auth-token': localStorage.getItem('token') }
      });
      setNewName('');
      setCreating(false);
      setOpen(false);
      navigate(`/maps/${res.data._id}`);
    } catch (e) { console.error('Create map failed', e); }
  };

  return (
    <div className="relative" ref={ref}>
      <button
        onClick={() => setOpen(o => !o)}
        className={`px-3 py-1.5 rounded-xl text-xs font-black uppercase tracking-wider transition-colors ${open ? 'bg-primary-blue text-white' : 'bg-white/80 dark:bg-gray-800/80 text-gray-600 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700'}`}
        title="Mind maps on this board"
      >
        Maps{maps.length ? ` (${maps.length})` : ''}
      </button>
      {open && (
        <div className="absolute right-0 top-full mt-1 w-64 bg-white dark:bg-gray-800 rounded-2xl shadow-2xl border border-gray-200 dark:border-gray-700 p-2 z-40 animate-fade-in">
          {maps.map(m => (
            <button
              key={m._id}
              onClick={() => navigate(`/maps/${m._id}`)}
              className="w-full text-left px-3 py-2 rounded-xl hover:bg-gray-50 dark:hover:bg-gray-700/60"
            >
              <span className="block text-sm font-bold text-gray-800 dark:text-gray-100">{m.name}</span>
              <span className="block text-[10px] uppercase tracking-wider text-gray-400">{m.nodeCount} topics</span>
            </button>
          ))}
          {!maps.length && !creating && (
            <p className="px-3 py-2 text-xs text-gray-400">No mind maps yet.</p>
          )}
          {creating ? (
            <div className="flex gap-1 p-1">
              <input
                value={newName}
                autoFocus
                onChange={(e) => setNewName(e.target.value)}
                onKeyDown={(e) => { if (e.key === 'Enter') createMap(); if (e.key === 'Escape') setCreating(false); }}
                placeholder="Map name…"
                className="flex-1 rounded-xl border border-gray-200 dark:border-gray-600 bg-gray-50 dark:bg-gray-900 px-2 py-1.5 text-sm outline-none focus:border-primary-blue text-gray-800 dark:text-gray-100"
              />
              <button onClick={createMap} className="px-3 rounded-xl bg-primary-blue text-white text-xs font-black">Go</button>
            </div>
          ) : (
            <button
              onClick={() => setCreating(true)}
              className="w-full mt-1 px-3 py-2 rounded-xl border-2 border-dashed border-gray-200 dark:border-gray-600 text-xs font-black uppercase tracking-wider text-gray-400 hover:border-primary-blue hover:text-primary-blue"
            >
              + New Mind Map
            </button>
          )}
        </div>
      )}
    </div>
  );
};

export default BoardMapsMenu;
